import { Controller } from "@hotwired/stimulus";
import { appendedToken, insertedToken } from "../../lib/fabric_bindings";
import { bindingScope } from "../../lib/variable_chain";
import { hasVariable } from "../../lib/variable_interpolation";

/**
 * Le sous-panneau Variables de la toolbox (plan media-template-generators §9) : il édite
 * l'EXPRESSION du binding `text` du calque sélectionné.
 *
 * L'expression est du texte libre interpolé — « $NOM vous accueille à $VILLE » : les tokens
 * du catalogue s'insèrent au curseur, le reste se tape. Ce qui est stocké dans le binding,
 * c'est l'expression ; ce qui s'affiche sur le canvas, c'est sa valeur d'exemple — le
 * document reste affichable tel quel (§4.2), et la règle §4.3 tient : un champ lié ne
 * s'édite que par son binding (fabric verrouille le texte), ou se délie.
 *
 * Insérer une variable, c'est l'écrire dans l'expression à un index, et l'index vient de qui
 * a la main : le texte est encore l'exemple de départ (placeholder) → elle remplace tout ;
 * la textbox est en édition sur la slide → à son curseur (le canvas répond à
 * `text:insert-token`) ; le champ du panneau a été visité → à son curseur, à la place de la
 * sélection ; sinon à la fin. Les deux surfaces éditent la même expression, chacune garde son
 * curseur : rien à synchroniser.
 *
 * Comme les autres sous-pages (overlay-form), il parle au canvas par événements window —
 * le panneau vit dans un turbo-frame, il n'a pas d'outlet vers l'éditeur.
 *
 * Naming: "composition-editor--variables" in HTML.
 */
export default class extends Controller {
  static targets = ["expr", "group"];

  static values = {
    // Les tokens du contrat sujet : le scope d'un binding se déduit de ses tokens — un
    // token sujet présent => subject (le relais d'expansion portera les tokens company
    // restants d'une expression mixte) ; sinon company.
    subjectTokens: Array,
    samples: Object,
    // Le texte est encore l'exemple posé à sa création : la première variable le remplace.
    placeholder: Boolean,
    // Le délai laisse le temps de taper une expression entière avant de la poser sur le
    // canvas : chaque application remesure le calque et relance la mise en page.
    debounce: { type: Number, default: 400 },
  };

  disconnect() {
    clearTimeout(this._timer);
  }

  // Le champ a été visité : son curseur compte (sinon on insère à la fin).
  touch() {
    this._touched = true;
  }

  insert(event) {
    const token = `$${event.currentTarget.dataset.token}`;
    const field = this.exprTarget;

    if (this.placeholderValue) return this.setExpression(token, token.length);

    // La textbox a la main : le canvas insère à SON curseur et rend l'expression.
    const detail = { token, values: this.samplesValue || {}, subjectTokens: this.subjectTokensValue || [] };
    window.dispatchEvent(new CustomEvent("text:insert-token", { detail }));
    if (typeof detail.expr === "string") {
      field.value = detail.expr;
      field.dispatchEvent(new CustomEvent("expression:set"));
      return;
    }

    const start = field.selectionStart ?? field.value.length;
    const { expr, caret } = this._touched
      ? insertedToken(field.value, token, start, field.selectionEnd ?? start)
      : appendedToken(field.value, token);
    this.setExpression(expr, caret);
  }

  // L'expression réécrite par le panneau : le champ garde la main, l'éditeur suit.
  setExpression(expr, caret) {
    const field = this.exprTarget;
    field.value = expr;
    field.dispatchEvent(new CustomEvent("expression:set"));
    field.focus();
    field.setSelectionRange(caret, caret);
    this.preview();
  }

  // Frappe : application différée — pas de bouton, l'éditeur suit (le canvas montre le résultat).
  preview() {
    this.placeholderValue = false;
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this.apply(), this.debounceValue);
  }

  // Les valeurs du lecteur changent (menu « Variables du document ») : l'insertion au
  // curseur de la slide les transmet au canvas.
  refreshSamples(event) {
    this.samplesValue = event.detail?.values || {};
  }

  // La recherche filtre les lignes par nom, token ou valeur ; un groupe vidé se cache.
  filter(event) {
    const query = event.currentTarget.value.trim().toLowerCase();
    this.groupTargets.forEach((group) => {
      let shown = 0;
      group.querySelectorAll("[data-search]").forEach((row) => {
        const match = !query || row.dataset.search.includes(query);
        row.hidden = !match;
        if (match) shown++;
      });
      group.hidden = shown === 0;
    });
  }

  apply() {
    clearTimeout(this._timer);
    const expr = this.exprTarget.value.trim();
    if (!expr) return this.unbind();

    // Pas de token ⇒ pas de liaison : c'est du texte, on le pose tel quel et on délie.
    if (!hasVariable(expr)) {
      this.dispatchToCanvas("text:set-content", { text: expr });
      this.dispatchToCanvas("text:unbind", { field: "text" });
      return;
    }

    // Le canvas stocke l'expression et l'interpole avec les valeurs de SON lecteur : on ne
    // lui envoie pas de valeur résolue, elle ne vaudrait que pour nous.
    this.dispatchToCanvas("text:bind", { field: "text", expr, scope: bindingScope(expr, this.subjectTokensValue || []) });
  }

  unbind() {
    this.dispatchToCanvas("text:unbind", { field: "text" });
  }

  dispatchToCanvas(name, detail) {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }
}
